import axios from 'axios';
import toast from 'react-hot-toast';

const DEFAULT_API_URL = '/api';

function getCurrentOrigin() {
  return typeof window !== 'undefined' && window.location?.origin
    ? window.location.origin
    : '';
}

export function cleanApiUrl(rawUrl = process.env.REACT_APP_API_URL) {
  const trimmed = (rawUrl || '').trim().replace(/\/+$/, '');
  if (!trimmed || trimmed === '/api') return DEFAULT_API_URL;

  const currentOrigin = getCurrentOrigin();

  try {
    const parsed = new URL(trimmed, currentOrigin || undefined);
    if (currentOrigin && parsed.origin === currentOrigin) {
      return DEFAULT_API_URL;
    }

    const normalizedPath = parsed.pathname.replace(/\/+$/, '');
    if (normalizedPath === '/api') return parsed.toString().replace(/\/+$/, '');

    parsed.pathname = `${normalizedPath}/api`;
    parsed.search = '';
    parsed.hash = '';
    return parsed.toString().replace(/\/+$/, '');
  } catch {
    return DEFAULT_API_URL;
  }
}

export function cleanApiBaseUrl(rawUrl = process.env.REACT_APP_API_URL) {
  const apiUrl = cleanApiUrl(rawUrl);
  return apiUrl === DEFAULT_API_URL ? '' : apiUrl.replace(/\/api$/, '');
}

const api = axios.create({
  baseURL: cleanApiUrl(),
});

api.interceptors.request.use(config => {
  const token = localStorage.getItem('cv_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Dedupe AI-unavailable toasts — one visible at a time instead of one per request
let aiToastId = null;

api.interceptors.response.use(
  r => r,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('cv_token');
      localStorage.removeItem('cv_user');
      window.location.href = '/login';
    }
    if (err.response?.status === 503 && err.response?.data?.error === 'AI_UNAVAILABLE') {
      const message = err.response.data.message || 'AI service is temporarily unavailable. Please try again in a moment.';
      if (aiToastId) toast.dismiss(aiToastId);
      aiToastId = toast(message, {
        icon: '⚠️',
        duration: 5000,
        style: { background: '#FEF3C7', color: '#78350F', border: '1px solid #FCD34D' }
      });
      err.isAIUnavailable = true;
      err.friendlyMessage = message;
    }
    return Promise.reject(err);
  }
);

export const auth = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
};

export const records = {
  list: (params) => api.get('/records', { params }),
  get: (id) => api.get(`/records/${id}`),
  upload: (formData) => api.post('/records/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, data) => api.patch(`/records/${id}`, data),
  publish: (id) => api.post(`/records/${id}/publish`),
  unpublish: (id) => api.post(`/records/${id}/unpublish`),
  delete: (id) => api.delete(`/records/${id}`),
  summary: (id, templateId) => api.post(`/records/${id}/summary`, { templateId }),
  reocr: (id) => api.post(`/records/${id}/ocr`),
};

/**
 * Ask a question and receive the answer as it is generated.
 *
 * axios cannot stream a response body, so this uses fetch directly and attaches
 * the same Bearer token the axios interceptor does. The server replies with
 * NDJSON — one JSON object per line — and instant answers (greetings, catalog
 * counts) arrive as a single token event, so there is one code path here rather
 * than two.
 *
 * Returns the accumulated answer text. THROWS on transport failure, so the caller
 * can fall back to the non-streaming request rather than showing an error for
 * something that would have worked.
 */
export async function askStream({ question, conversation_id = null, onMeta, onToken, onDone }) {
  const token = localStorage.getItem('cv_token');
  const res = await fetch(`${cleanApiUrl()}/rag/ask`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify({ question, conversation_id, stream: true })
  });

  if (!res.ok || !res.body) {
    throw new Error(`Stream request failed with ${res.status}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let answer = '';

  // Lines can be split across network chunks, so only whole lines are parsed and
  // the remainder is carried over.
  const handle = (line) => {
    if (!line.trim()) return;
    let msg;
    try { msg = JSON.parse(line); } catch { return; }
    if (msg.type === 'meta') onMeta?.(msg);
    else if (msg.type === 'token') { answer += msg.t; onToken?.(msg.t, answer); }
    else if (msg.type === 'done') onDone?.(msg);
    else if (msg.type === 'error') throw new Error(msg.message || 'The answer failed part-way through');
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';   // keep the partial line
    for (const line of lines) handle(line);
  }
  if (buffer) handle(buffer);

  return answer;
}

// Conversation threads for Ask AI. Scoped to the caller server-side — there is
// no user id in any of these paths.
export const chat = {
  listConversations: () => api.get('/chat/conversations'),
  getConversation: (id) => api.get(`/chat/conversations/${id}`),
  rename: (id, title) => api.patch(`/chat/conversations/${id}`, { title }),
  remove: (id) => api.delete(`/chat/conversations/${id}`),
};

export const search = {
  query: (params) => api.get('/search', { params }),
  facets: () => api.get('/search/facets'),
};

export const publicApi = {
  search: (params) => api.get('/public/search', { params }),
  getRecord: (id) => api.get(`/public/records/${id}`),
  facets: () => api.get('/public/facets'),
};

export const migration = {
  jobs: () => api.get('/migration/jobs'),
  getJob: (id) => api.get(`/migration/jobs/${id}`),
  simulate: (data) => api.post('/migration/simulate', data),
  upload: (formData) => api.post('/migration/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

export const rag = {
  ingest: (id) => api.post(`/rag/ingest/${id}`),
  ingestAll: () => api.post('/rag/ingest-all'),
  ask: (question, top_k, conversation_id) => api.post('/rag/ask', { question, top_k, conversation_id }),
  askPublic: (question, top_k) => api.post('/rag/ask-public', { question, top_k }),
  status: () => api.get('/rag/status'),
};

export const nlSearch = {
  generate: (question) => api.post('/nl-search/generate', { question }),
  execute: (sql, database = 'records') => api.post('/nl-search/execute', { sql, database }),
};

export const redaction = {
  analyze: (recordId) => api.post(`/redaction/analyze/${recordId}`),
  apply: (jobId, selected_entities) => api.post(`/redaction/apply/${jobId}`, { selected_entities }),
  jobs: (recordId) => api.get(`/redaction/jobs/${recordId}`),
};

export const admin = {
  stats: () => api.get('/admin/stats'),
  users: () => api.get('/admin/users'),
  audit: () => api.get('/admin/audit'),
  demoRequests: () => api.get('/demo-requests'),
  updateDemoRequest: (id, data) => api.patch(`/demo-requests/${id}`, data),
};

export default api;
